import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { config } from '../../../../config';
import { ArticleUpsertDto } from '../../../../shared/types';

const ARTICLE_DRAFT_STORAGE_KEY_PREFIX = 'articleDraft';
const LEGACY_ARTICLE_DRAFT_STORAGE_KEY = ARTICLE_DRAFT_STORAGE_KEY_PREFIX;

interface NormalisedHtmlTextNode {
    type: Node['nodeType'];
    text: string;
}

interface NormalisedHtmlElementNode {
    type: Node['nodeType'];
    name: string;
    attributes: [string, string][];
    children: NormalisedHtmlNodeUnion[];
}

type NormalisedHtmlNodeUnion =
    NormalisedHtmlTextNode | NormalisedHtmlElementNode;

const normaliseArticleDraft = (
    articleData: ArticleUpsertDto
): ArticleUpsertDto => JSON.parse(JSON.stringify(articleData));

const normaliseElementStyle = (element: HTMLElement) =>
    Array.from({ length: element.style.length }, (_, index) => {
        const property = element.style.item(index);

        return [
            property,
            element.style.getPropertyValue(property).trim(),
            element.style.getPropertyPriority(property),
        ];
    });

const normaliseArticleDraftHtml = (html: string) => {
    // Froala re-serialises equivalent markup as it initialises and edits.
    // Compare the parsed (normalised) structure to avoid phantom drafts.
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const normaliseNode = (node: Node): NormalisedHtmlNodeUnion => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return {
                type: node.nodeType,
                text: node.textContent ?? '',
            };
        }

        const element = node as HTMLElement;
        const attributes = Array.from(element.attributes)
            .map((attribute) => [
                attribute.name,
                attribute.name === 'style'
                    ? JSON.stringify(normaliseElementStyle(element))
                    : attribute.value,
            ])
            .sort(([firstName], [secondName]) =>
                firstName.localeCompare(secondName)
            ) as [string, string][];

        return {
            type: node.nodeType,
            name: element.tagName.toLowerCase(),
            attributes,
            children: Array.from(element.childNodes).map(normaliseNode),
        };
    };

    return JSON.stringify(Array.from(doc.body.childNodes).map(normaliseNode));
};

const getArticleDraftSignature = (articleData: ArticleUpsertDto) =>
    JSON.stringify({
        ...normaliseArticleDraft(articleData),
        html: normaliseArticleDraftHtml(articleData.html),
    });

export const getArticleDraftStorageKey = (articleId: string | undefined) =>
    `${ARTICLE_DRAFT_STORAGE_KEY_PREFIX}:${articleId ?? 'new'}`;

export const clearArticleDraftStorage = (articleId: string | undefined) => {
    localStorage.removeItem(getArticleDraftStorageKey(articleId));
    localStorage.removeItem(LEGACY_ARTICLE_DRAFT_STORAGE_KEY);
};

const getStoredArticleDraft = (articleDraftStorageKey: string) => {
    const storageKeys = [
        articleDraftStorageKey,
        LEGACY_ARTICLE_DRAFT_STORAGE_KEY,
    ];

    for (const storageKey of storageKeys) {
        const draftArticleDataFromLocalStorage =
            localStorage?.getItem(storageKey);

        if (!draftArticleDataFromLocalStorage) continue;

        try {
            return JSON.parse(draftArticleDataFromLocalStorage);
        } catch {
            // A broken local draft should not stop the editor from loading
            localStorage.removeItem(storageKey);
        }
    }
};

interface UseArticleDraftAutoSaveArgs {
    articleId: string | undefined;
    templateId: string | undefined;
    isPageReady: boolean;
    generateArticleDto: () => ArticleUpsertDto;
    setInitialFormValues: (values: ArticleUpsertDto) => void;
}

export const useArticleDraftAutoSave = ({
    articleId,
    templateId,
    isPageReady,
    generateArticleDto,
    setInitialFormValues,
}: UseArticleDraftAutoSaveArgs) => {
    const articleDraftStorageKey = getArticleDraftStorageKey(articleId);

    const draftCheckCompletedRef = useRef(false);
    const articleDraftBaselineSignatureRef = useRef<string | undefined>(
        undefined
    );
    const [isDraftPromptVisible, setIsDraftPromptVisible] =
        useState<boolean>(false);
    const [initialArticleValuesReady, setInitialArticleValuesReady] =
        useState<boolean>(false);
    const [draftResumeCount, setDraftResumeCount] = useState(0);

    // Capture the initial baseline once the article/template has filled the form.
    const isDraftCheckReady = initialArticleValuesReady && isPageReady;

    const markInitialArticleValuesReady = useCallback(() => {
        setInitialArticleValuesReady(true);
    }, []);

    useEffect(() => {
        draftCheckCompletedRef.current = false;
        articleDraftBaselineSignatureRef.current = undefined;
        setIsDraftPromptVisible(false);
        setInitialArticleValuesReady(false);
        setDraftResumeCount(0);
    }, [articleDraftStorageKey, templateId]);

    useEffect(() => {
        if (!templateId && !articleId && isPageReady) {
            markInitialArticleValuesReady();
        }
    }, [articleId, isPageReady, markInitialArticleValuesReady, templateId]);

    // Only consider whether to present the resume draft prompt on initial load only
    useEffect(() => {
        if (isDraftCheckReady && !draftCheckCompletedRef.current) {
            draftCheckCompletedRef.current = true;

            const draftArticleData = getStoredArticleDraft(
                articleDraftStorageKey
            );
            const articleData = normaliseArticleDraft(generateArticleDto());
            const articleDraftSignature = getArticleDraftSignature(articleData);
            articleDraftBaselineSignatureRef.current = articleDraftSignature;

            if (
                draftArticleData &&
                !templateId &&
                articleDraftSignature !==
                    getArticleDraftSignature(draftArticleData) &&
                articleData?._id === draftArticleData?._id
            ) {
                setIsDraftPromptVisible(true);
            }
        }
    }, [
        articleDraftStorageKey,
        generateArticleDto,
        isDraftCheckReady,
        templateId,
    ]);

    useEffect(() => {
        if (isDraftCheckReady) {
            const timeoutId = setTimeout(() => {
                const articleData = normaliseArticleDraft(generateArticleDto());
                const hasArticleChanges =
                    articleDraftBaselineSignatureRef.current
                        ? getArticleDraftSignature(articleData) !==
                          articleDraftBaselineSignatureRef.current
                        : false;

                if (hasArticleChanges)
                    localStorage.setItem(
                        articleDraftStorageKey,
                        JSON.stringify(articleData)
                    );

                if (!hasArticleChanges && !isDraftPromptVisible) {
                    localStorage.removeItem(articleDraftStorageKey);
                }
            }, config.editorAutoSaveInterval);

            return () => clearTimeout(timeoutId);
        }
    }, [
        articleDraftStorageKey,
        generateArticleDto,
        isDraftCheckReady,
        isDraftPromptVisible,
    ]);

    const draftHandlers = useMemo(() => {
        if (!templateId && isDraftPromptVisible) {
            return {
                onResume: () => {
                    const draftArticleData = getStoredArticleDraft(
                        articleDraftStorageKey
                    );

                    if (draftArticleData) {
                        setInitialFormValues(draftArticleData);
                        setDraftResumeCount((count) => count + 1);
                        setIsDraftPromptVisible(false);
                    }
                },
                onDiscard: () => {
                    clearArticleDraftStorage(articleId);
                    articleDraftBaselineSignatureRef.current =
                        getArticleDraftSignature(
                            normaliseArticleDraft(generateArticleDto())
                        );
                    setIsDraftPromptVisible(false);
                },
            };
        }
    }, [
        generateArticleDto,
        articleId,
        articleDraftStorageKey,
        isDraftPromptVisible,
        setInitialFormValues,
        templateId,
    ]);

    return {
        draftHandlers,
        // Froala only reads initialHtml on mount. Include the resume count so a
        // restored draft remounts the editor even though the article ID is unchanged.
        articleEditorKey: `${articleId ?? 'new'}-${draftResumeCount}`,
        markInitialArticleValuesReady,
    };
};
